import React from 'react';
import { Scenario, Category } from '../types';

interface ErrorListModalProps {
  isOpen: boolean;
  onClose: () => void;
  categories: Category[];
  onScenarioSelect: (scenario: Scenario) => void;
}

const ErrorListModal: React.FC<ErrorListModalProps> = ({ isOpen, onClose, categories, onScenarioSelect }) => {
  if (!isOpen) return null;
  
  // Zbierz wszystkie scenariusze z błędami
  const failedScenarios = categories.flatMap(category =>
    category.scenarios.filter(scenario => scenario.Status === 'Failed')
      .map(scenario => ({
        ...scenario,
        categoryName: category.name
      }))
  );
  
  // Sortuj błędy alfabetycznie według kategorii
  failedScenarios.sort((a, b) => {
    if (a.categoryName !== b.categoryName) {
      return a.categoryName.localeCompare(b.categoryName);
    }
    return a['Nazwa testu'].localeCompare(b['Nazwa testu']);
  });
  
  // Grupuj według kategorii
  const scenariosByCategory: { [key: string]: (Scenario & { categoryName: string })[] } = {};
  failedScenarios.forEach(scenario => {
    if (!scenariosByCategory[scenario.categoryName]) {
      scenariosByCategory[scenario.categoryName] = [];
    }
    scenariosByCategory[scenario.categoryName].push(scenario);
  });

  // Funkcja eksportująca błędy do CSV
  const exportToCSV = () => {
    if (failedScenarios.length === 0) return;
    
    // Przygotuj dane CSV
    const header = ['ID testu', 'Kategoria', 'Nazwa testu', 'Oczekiwany rezultat', 'Notatki'].join(',');
    const rows = failedScenarios.map(scenario => [
      `"${scenario['ID testu']}"`,
      `"${scenario.categoryName}"`,
      `"${scenario['Nazwa testu'].replace(/"/g, '""')}"`,
      `"${scenario['Oczekiwany rezultat'].replace(/"/g, '""')}"`,
      `"${(scenario.Notes || '').replace(/"/g, '""')}"`
    ].join(','));
    
    const csvContent = [header, ...rows].join('\n');
    
    // Utwórz link do pobrania
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    // Przygotuj nazwę pliku z datą
    const date = new Date().toISOString().slice(0, 10);
    link.setAttribute('href', url);
    link.setAttribute('download', `lista_bledow_${date}.csv`);
    
    // Wyzwól pobieranie
    document.body.appendChild(link);
    link.click();
    
    // Posprzątaj
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-slate-600 flex justify-between items-center bg-gradient-to-r from-red-500 to-red-600 text-white">
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Lista błędów
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          {failedScenarios.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-gray-600 dark:text-gray-300 text-lg">Nie znaleziono żadnych błędów!</p>
              <p className="text-gray-500 dark:text-gray-400 mt-2">Wszystkie testy zostały zaliczone lub nie zostały jeszcze wykonane.</p>
            </div>
          ) : (
            <div>
              <div className="flex justify-between items-center mb-4">
                <div className="text-gray-700 dark:text-gray-300">
                  Znaleziono <span className="font-semibold text-red-600 dark:text-red-400">{failedScenarios.length}</span> scenariuszy z błędami
                </div>
                <button 
                  onClick={exportToCSV} 
                  className="flex items-center gap-1 px-3 py-1 bg-gray-100 dark:bg-slate-600 hover:bg-gray-200 dark:hover:bg-slate-500 text-gray-700 dark:text-gray-200 font-medium rounded-lg transition-colors text-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Eksportuj do CSV
                </button>
              </div>
              
              <div className="space-y-6">
                {Object.entries(scenariosByCategory).map(([categoryName, scenarios]) => (
                  <div key={categoryName} className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      {categoryName} <span className="text-sm text-red-600 dark:text-red-400 ml-2">({scenarios.length})</span>
                    </h3>
                    
                    <div className="space-y-2">
                      {scenarios.map(scenario => (
                        <div 
                          key={scenario['ID testu']} 
                          className="bg-white dark:bg-slate-700 p-4 rounded-lg shadow-sm hover:shadow-md dark:border dark:border-slate-600 transition-shadow cursor-pointer"
                          onClick={() => {
                            onScenarioSelect(scenario);
                            onClose();
                          }}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white mb-1">{scenario['Nazwa testu']}</div>
                              <div className="text-sm text-gray-600 dark:text-gray-400">{scenario['ID testu']}</div>
                            </div>
                            <div className="flex items-center bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 px-2 py-1 rounded-full text-xs font-medium">
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                              Błąd
                            </div>
                          </div>
                          
                          {scenario.Notes && (
                            <div className="mt-3 text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-slate-600/60 p-3 rounded-md">
                              <div className="font-medium text-gray-900 dark:text-white mb-1">Notatki:</div>
                              {scenario.Notes}
                            </div>
                          )}
                          
                          <div className="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">
                            Kliknij, aby przejść do testu
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="p-4 border-t border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700/50 flex justify-end space-x-4">
          {failedScenarios.length > 0 && (
            <button
              onClick={exportToCSV}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Eksportuj do CSV
            </button>
          )}
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 dark:bg-slate-600 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-500 transition-colors"
          >
            Zamknij
          </button>
        </div>
      </div>
    </div>
  );
};

export default ErrorListModal; 